import { Avatar, Box, Button, Chip, Drawer, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { v4 } from "uuid";
import { ChevronLeft } from "@mui/icons-material";
import { IProdutoPedido } from "../../../pedidos/interface/IProdutoPedido";
import { IProduto } from "../../interfaces/IProduto";
import { TipoProduto } from "../../enum/TipoProduto";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { useTamanhoTela } from "../../../core/hooks/useTamanhoTela";
import { BodyDetalhesContainer, DetalhesContainer, HeaderDetalhesContainer } from "../CardPreparadoOperacao/styles";

interface DrawerPesoOperacaoProps {
    open: boolean;
    toggleDrawer: () => void;
    produto: IProduto | IProdutoPedido;
    onSubmit: (produto: IProdutoPedido) => void;
    uuid?: string;
    gramasIniciais?: number;
}

export const DrawerPesoOperacao = ({
    open,
    toggleDrawer,
    produto,
    onSubmit,
    uuid,
    gramasIniciais,
}: DrawerPesoOperacaoProps) => {
    const [gramas, setGramas] = useState<number>(gramasIniciais ?? 0);
    const { real } = useMoeda();
    const { modeloTela } = useTamanhoTela();

    const configuracaoPeso = produto.configuracaoPeso ?? produto.configuracao;
    const precoPorKg = configuracaoPeso?.precoPorKg ?? 0;
    const valorTeto = configuracaoPeso?.valorTeto;
    const valorBruto = (gramas / 1000) * precoPorKg;
    const precoFinal = valorTeto ? Math.min(valorBruto, valorTeto) : valorBruto;
    const tetoAtingido = !!valorTeto && valorBruto >= valorTeto;

    const handleToggleDrawer = () => {
        if (!uuid) {
            setGramas(0);
        }
        toggleDrawer();
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const dadosProduto: IProdutoPedido = {
            uuid: uuid ?? v4(),
            uuidProdutoReferencia: produto.uuid,
            descricao: produto.descricao,
            tipo: TipoProduto.peso,
            preco: precoFinal,
            gramas,
            quantidade: 1,
            imagens: produto.imagens,
            configuracaoPeso: configuracaoPeso as any,
        };

        onSubmit(dadosProduto);
        handleToggleDrawer();
    };

    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={handleToggleDrawer}
            sx={{ borderRadius: 0, zIndex: 10002 }}
        >
            <form onSubmit={handleSubmit}>
                <DetalhesContainer>
                    {modeloTela !== "desktop" ? (
                        <Button onClick={handleToggleDrawer} sx={{ height: "70px", width: '100%', marginBottom: '2rem' }} startIcon={<ChevronLeft />}>
                            Voltar
                        </Button>
                    ) : ''}
                    <HeaderDetalhesContainer>
                        {produto.imagens?.length ? (
                            <Avatar
                                alt={produto.descricao}
                                src={`${produto.imagens[0].url}`}
                                sx={{ width: 90, height: 90 }}
                            />
                        ) : (
                            <Avatar
                                alt={produto.descricao}
                                src={`${process.env.PUBLIC_URL}/images/produtos/generico.png`}
                                sx={{ width: 90, height: 90 }}
                            />
                        )}
                        <div>
                            <Typography gutterBottom variant="h5" fontWeight={"bold"} component="div">
                                {produto.descricao}
                            </Typography>
                            <Typography gutterBottom variant="h6" component="div" color="text.secondary">
                                {real(precoPorKg)} / kg
                            </Typography>
                        </div>
                    </HeaderDetalhesContainer>
                    <BodyDetalhesContainer>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
                            <TextField
                                label="Peso (gramas)"
                                type="number"
                                value={gramas || ''}
                                onChange={(e) => setGramas(Math.max(0, parseInt(e.target.value) || 0))}
                                inputProps={{ min: 1, step: 1 }}
                                placeholder="Digite o peso em gramas"
                                autoFocus
                                fullWidth
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="h5" fontWeight="bold">
                                    {real(precoFinal)}
                                </Typography>
                                {tetoAtingido && (
                                    <Chip label="Buffet livre" color="success" size="small" />
                                )}
                            </Box>
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={gramas <= 0}
                                sx={{ height: "5rem" }}
                            >
                                Adicionar ao pedido
                            </Button>
                        </Box>
                    </BodyDetalhesContainer>
                </DetalhesContainer>
            </form>
        </Drawer>
    );
};
